const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const parseMaybeJSON = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (
    !(trimmed.startsWith('{') || trimmed.startsWith('[')) ||
    !(trimmed.endsWith('}') || trimmed.endsWith(']'))
  ) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const coerceString = (value) => {
  if (typeof value === 'string') return value.trim();
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((v) => coerceString(v)).filter(Boolean);
};

const hasMarkdownStructure = (text) =>
  typeof text === 'string' && /\*\*[^*]+:\*\*/.test(text) && text.length > 20;

const extractReflectionCore = (input) => {
  const parsed = parseMaybeJSON(input);

  if (typeof parsed === 'string') {
    const nested = parseMaybeJSON(parsed);
    if (nested !== parsed) return extractReflectionCore(nested);
    return { reflectionText: parsed };
  }

  if (!isObject(parsed)) {
    return { reflectionText: coerceString(parsed) };
  }

  const reflectionCandidate =
    parsed.aiReflection ||
    parsed.reflection ||
    parsed.response ||
    parsed.message ||
    parsed.text ||
    parsed.content ||
    '';

  const reflectionText =
    typeof reflectionCandidate === 'string'
      ? coerceString(reflectionCandidate)
      : coerceString(parseMaybeJSON(reflectionCandidate));

  return {
    reflectionText,
    insights: normalizeStringArray(parsed.insights),
    patterns: normalizeStringArray(parsed.patterns),
    recommendations: normalizeStringArray(parsed.recommendations),
    keyLearnings: normalizeStringArray(parsed.keyLearnings || parsed.key_learnings),
    challengesFaced: normalizeStringArray(parsed.challengesFaced || parsed.challenges_faced),
    solutionsApplied: normalizeStringArray(parsed.solutionsApplied || parsed.solutions_applied),
    skillsPracticed: normalizeStringArray(parsed.skillsPracticed || parsed.skills_practiced),
    emotionalTone: coerceString(parsed.emotionalTone || parsed.emotion || parsed.tone),
  };
};

const section = (title, body) => `**${title}:**\n${body}`;

const listBody = (items) => items.map((item) => `- ${item}`).join('\n');

const paragraphBody = (text) => text || 'No reflection provided.';

export const toMarkdownTaskReflection = (payload) => {
  const reflection = extractReflectionCore(payload);

  const reflectionBody = paragraphBody(reflection.reflectionText);
  const insights = reflection.insights || [];
  const patterns = reflection.patterns || [];
  const recommendations = reflection.recommendations || [];
  const learnings = reflection.keyLearnings || [];

  const parts = [section('Reflection', reflectionBody)];

  if (insights.length > 0) parts.push(section('Key Insights', listBody(insights)));
  if (patterns.length > 0) parts.push(section('Patterns Observed', listBody(patterns)));
  if (recommendations.length > 0) parts.push(section('Recommendations', listBody(recommendations)));
  if (learnings.length > 0) parts.push(section('Key Learnings', listBody(learnings)));

  return parts.join('\n\n').trim();
};

export const normalizeTaskReflectionPayload = (payload) => {
  const extracted = extractReflectionCore(payload);
  const markdown = hasMarkdownStructure(extracted.reflectionText)
    ? extracted.reflectionText
    : toMarkdownTaskReflection(payload);

  return {
    aiReflection: markdown,
    insights: extracted.insights || [],
    patterns: extracted.patterns || [],
    recommendations: extracted.recommendations || [],
    keyLearnings: extracted.keyLearnings || [],
    challengesFaced: extracted.challengesFaced || [],
    solutionsApplied: extracted.solutionsApplied || [],
    skillsPracticed: extracted.skillsPracticed || [],
    emotionalTone: extracted.emotionalTone || 'neutral',
  };
};

export const normalizeTaskReflectionText = (value) =>
  normalizeTaskReflectionPayload(value).aiReflection;
